import alertbox from '@/utils/alertbox'
import { reactive, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'

export const validators = {
  required:
    (fieldKey, { multiple } = {}) =>
    ({ value, t }) => {
      let message
      if (fieldKey) {
        message = multiple
          ? t('validation.rules.requiredMultiple', { field: t(fieldKey) })
          : t('validation.rules.required', { field: t(fieldKey) })
      } else {
        message = true
      }
      if (value === null || value === undefined) return message
      if (typeof value === 'string' && !value.trim()) return message
      if (Array.isArray(value) && value.length === 0) return message
      return null
    },

  email:
    (fieldKey) =>
    ({ value, t }) => {
      const message = t('validation.rules.email', { field: t(fieldKey) })
      if (!value) return null
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(value) ? null : message
    },

  minLength:
    (min, fieldKey) =>
    ({ value, t }) => {
      const message = t('validation.rules.minLength', { field: t(fieldKey), min })
      if (isNaN(Number(value))) return null
      return value.length >= min ? null : message
    },

  maxLength:
    (max, fieldKey) =>
    ({ value, t }) => {
      const message = t('validation.rules.maxLength', { field: t(fieldKey), max })
      if (isNaN(Number(value))) return null
      return value.length <= max ? null : message
    },

  url:
    (fieldKey) =>
    ({ value, t }) => {
      const message = t('validation.rules.url', { field: t(fieldKey) })
      if (!value || !value.trim()) return null
      try {
        const url = new URL(value)
        if (!['http:', 'https:'].includes(url.protocol)) {
          return message
        }
        if (url.hostname.endsWith('.')) return message
        if (!url.hostname.includes('.')) return message
        return null
      } catch {
        return message
      }
    },

  pattern:
    (regexString, fieldKey) =>
    ({ value, t }) => {
      const message = t('validation.rules.pattern', { field: t(fieldKey) })
      if (!value) return null
      const regex = new RegExp(regexString)
      return regex.test(value) ? null : message
    },

  numeric:
    (fieldKey, { multiple } = {}) =>
    ({ value, t }) => {
      const message = multiple
        ? t('validation.rules.numericMultiple', { field: t(fieldKey) })
        : t('validation.rules.numeric', { field: t(fieldKey) })
      if (value === null || value === undefined || value === '') return null
      return isNaN(value) ? message : null
    },

  wholeNumber:
    (fieldKey) =>
    ({ value, t }) => {
      const message = t('validation.rules.wholeNumber', { field: t(fieldKey) })
      if (isNaN(Number(value))) return null
      return Number.isInteger(Number(value)) ? null : message
    },

  min:
    (min, fieldKey, { multiple } = {}) =>
    ({ value, t }) => {
      const message = multiple
        ? t('validation.rules.minMultiple', { field: t(fieldKey), min })
        : t('validation.rules.min', { field: t(fieldKey), min })
      if (isNaN(Number(value))) return null
      return Number(value) >= min ? null : message
    },

  max:
    (max, fieldKey) =>
    ({ value, t }) => {
      const message = t('validation.rules.max', { field: t(fieldKey), max })
      if (isNaN(Number(value))) return null
      return Number(value) <= max ? null : message
    },

  custom:
    (fn, fieldKey, fieldChildKeys) =>
    ({ value, data, t }) => {
      const resolvedKeys = {}
      for (const key in fieldChildKeys) {
        resolvedKeys[key] = t(fieldChildKeys[key])
      }
      const message = t(fieldKey, resolvedKeys)
      return fn(value, data) ? null : message
    }
}

export function useValidation(rules = {}, t = useI18n().t) {
  const state = reactive({
    fieldErrors: {}
  })

  const validate = async (data) => {
    let errors = {}
    Object.keys(rules).forEach((fieldName) => {
      const fieldRules = rules[fieldName]
      const value = data[fieldName]

      if (!Array.isArray(fieldRules)) {
        console.warn(`Rules for field "${fieldName}" must be an array`)
        return
      }

      for (const rule of fieldRules) {
        const error = rule({ value, data, t })
        if (error) {
          errors[fieldName] = error
          break
        }
      }
    })

    if (Object.entries(errors).filter((x) => x[1] !== true).length > 0) {
      const uniqueErrors = [...new Set(Object.values(errors).filter((x) => x !== true))]
      alertbox({
        title: 'Please Check Your Input',
        html: `<ul><li>${uniqueErrors.join('</li><li>')}</li></ul>`
      })
    }

    state.fieldErrors = errors
    return Object.keys(errors).length > 0 ? errors : undefined
  }

  const validateMultiple = async (data) => {
    const errors = []
    Object.keys(rules).forEach((fieldName) => {
      const fieldRules = rules[fieldName]

      if (!Array.isArray(fieldRules)) {
        console.warn(`Rules for field "${fieldName}" must be an array`)
        return
      }

      for (let i = 0; i < data.length; i++) {
        const item = data[i]
        const value = getNested(item, fieldName)
        for (const rule of fieldRules) {
          const error = rule({ value, data, t })
          if (error) {
            errors[i] = errors[i] || {}
            errors[i][fieldName] = error
          }
        }
      }
    })

    if (errors.length > 0) {
      const uniqueErrors = [...new Set(errors.flatMap((e) => Object.values(e)))]
      await alertbox({
        title: 'Please Check Your Input',
        html: `<ul><li>${uniqueErrors.join('</li><li>')}</li></ul>`
      })
    }

    state.fieldErrors = errors
    return errors
  }

  function getNested(obj, path) {
    return path.split('.').reduce((acc, key) => acc?.[key], obj)
  }

  return {
    ...toRefs(state),
    validate,
    validateMultiple
  }
}
